import React, { useState, useEffect, useRef } from "react";
import { useSession } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { Wall, X, ArrowSquareOut, MagnifyingGlass } from "@phosphor-icons/react";
import { Spinner } from "@heroui/react";

interface RenovationProjectOption {
  id: number;
  title: string;
}

interface RenovationProjectSectionProps {
  ticketId: number;
  renovationProject?: { id: number; title: string; status?: string } | null;
  onUpdate: (updatedTicket: any) => void;
}

export const RenovationProjectSection: React.FC<RenovationProjectSectionProps> = ({
  ticketId,
  renovationProject,
  onUpdate,
}) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [projects, setProjects] = useState<RenovationProjectOption[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isSearchOpen) return;

    const loadProjects = async () => {
      setIsLoadingProjects(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/project/minimal`,
          { method: "GET" },
          session,
        );
        if (response.ok) {
          const data = await response.json();
          setProjects(data);
        }
      } catch (error) {
        toast.error("Fout bij laden renovatieprojecten");
      } finally {
        setIsLoadingProjects(false);
      }
    };

    loadProjects();
  }, [isSearchOpen, session]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsSearchOpen(false);
        setSearchText("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredProjects = projects.filter((p) =>
    p.title.toLowerCase().includes(searchText.toLowerCase()) ||
    p.id.toString().includes(searchText),
  );

  const handleLink = async (project: RenovationProjectOption) => {
    setIsSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticketId}/renovation-project`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ renovationProjectId: project.id }),
        },
        session,
      );
      if (!response.ok) throw new Error("Koppeling mislukt");
      const updated = await response.json();
      onUpdate(updated);
      toast.success(`Gekoppeld aan "${project.title}"`);
      setIsSearchOpen(false);
      setSearchText("");
    } catch {
      toast.error("Fout bij koppelen renovatieproject");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnlink = async () => {
    setIsSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticketId}/renovation-project`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ renovationProjectId: null }),
        },
        session,
      );
      if (!response.ok) throw new Error("Ontkoppelen mislukt");
      const updated = await response.json();
      onUpdate(updated);
      toast.success("Renovatieproject ontkoppeld");
    } catch {
      toast.error("Fout bij ontkoppelen renovatieproject");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="border-neutral-primary/10 border-b px-4 py-3">
      <div className="mb-2 flex items-center gap-2">
        <Wall size={14} className="text-amber-600 shrink-0" weight="duotone" />
        <span className="text-neutral-secondary-mid text-xs font-medium uppercase tracking-wide">
          Renovatieproject
        </span>
      </div>

      {renovationProject ? (
        <div className="flex items-center gap-2">
          <div className="bg-amber-50 border-amber-200 flex min-w-0 flex-1 items-center gap-2 rounded-md border px-3 py-2">
            <Wall size={14} className="text-amber-600 shrink-0" weight="duotone" />
            <span className="text-amber-900 min-w-0 flex-1 truncate text-sm font-medium">
              {renovationProject.title}
            </span>
            <button
              onClick={() => navigate(`/renovation/${renovationProject.id}`)}
              className="text-amber-600 hover:text-amber-800 shrink-0 transition-colors"
              title="Open renovatieproject"
            >
              <ArrowSquareOut size={14} />
            </button>
          </div>
          <button
            onClick={handleUnlink}
            disabled={isSaving}
            className="text-neutral-secondary-mid hover:text-brick-primary shrink-0 rounded p-1 transition-colors disabled:opacity-50"
            title="Ontkoppel renovatieproject"
          >
            {isSaving ? <Spinner size="sm" /> : <X size={14} />}
          </button>
        </div>
      ) : (
        <div ref={searchRef} className="relative">
          <button
            onClick={() => setIsSearchOpen((v) => !v)}
            className="border-neutral-primary/20 text-neutral-secondary-mid hover:border-moss-primary hover:text-moss-primary flex w-full items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm transition-colors"
          >
            <MagnifyingGlass size={14} />
            <span>Koppel renovatieproject...</span>
          </button>

          {isSearchOpen && (
            <div className="border-neutral-primary/20 absolute top-full z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
              <div className="border-neutral-primary/10 border-b p-2">
                <input
                  autoFocus
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Zoek op naam of ID..."
                  className="text-neutral-primary placeholder:text-neutral-secondary-mid w-full rounded px-2 py-1 text-sm outline-none"
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {isLoadingProjects ? (
                  <div className="flex justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : filteredProjects.length === 0 ? (
                  <div className="text-neutral-secondary-mid py-3 text-center text-sm">
                    Geen projecten gevonden
                  </div>
                ) : (
                  filteredProjects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => handleLink(project)}
                      disabled={isSaving}
                      className="hover:bg-moss-primary/5 flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors disabled:opacity-50"
                    >
                      <Wall size={14} className="text-amber-500 shrink-0" weight="duotone" />
                      <span className="text-neutral-primary min-w-0 flex-1 truncate">
                        {project.title}
                      </span>
                      <span className="text-neutral-secondary-mid shrink-0 text-xs">
                        #{project.id}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
